// @flow
import React, { useEffect } from 'react';
import type { Element } from 'react';

import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import { connect } from 'react-redux';
import { changeTitle } from '@redux/components/Metadata/actions';

import { humanize } from '@lib/helpers';
import { isEmpty } from 'lodash';
import { useTranslation, Router } from '@i18n';
import { FINDER_FACILITY } from '@routes';

import { ITEMS_PER_PAGE } from '../redux/constants';

import SearchResultListItem from './SearchResultListItem';

import {
  StyledWrapper,
  StyledHeadlineText,
  StyledList,
  StyledSpinner,
  StyledSpinnerContainer,
  StyledH1Container,
  StyledResetFilter,
  StyledResultFilter
} from './styles';

import type { Props, TitleTuple } from './types';

const SearchResultList = ({
  isMapVisible,
  itemList,
  numberOfPages,
  address_city,
  departments,
  loading,
  hasActiveFilters,
  changeTitle
}: Props): Element<any> => {
  const { t } = useTranslation('Facilities');
  const totalItems = numberOfPages * ITEMS_PER_PAGE;
  const numberOfObjects: number = numberOfPages > 1 ? totalItems : itemList.length;
  const listItemDesktopSize = isMapVisible ? 5 : 3;

  const currentSpecialties: ?string = t('Filters.Specialties', { returnObjects: true })[
    departments
  ];

  const titleStringSuffix: string = [
    ['inCity', 'city', address_city],
    ['withSpecialties', 'specialties', currentSpecialties]
  ].reduce(
    (suffix: string, [translationKey, attributeName, attributeValue]: TitleTuple): string => {
      if (isEmpty(attributeValue)) {
        return suffix;
      }

      return suffix + `  ${t(translationKey, { [attributeName]: attributeValue })}`;
    },
    ''
  );

  useEffect((): void => {
    changeTitle({
      IN: {
        en: t('Headlines.Facilities', '') + titleStringSuffix
      }
    });
  }, [t('Headlines.Facilities', ''), titleStringSuffix]);

  const HeadLines: {
    Facilities: string,
  } = t('Facilities:Headlines', {
    returnObjects: true,
    department: departments && departments !== 'all-facilities' ? (humanize(departments.split('_'))) : '',
    city: address_city && address_city !== 'everywhere' ? address_city : 'the World',
    facility: 'Hospitals'
  });

  const clearFilters = () => {
    Router.pushRoute(FINDER_FACILITY, { pageNumber: 1 });
  };

  return (
    <StyledWrapper>
      <StyledHeadlineText>
        <StyledH1Container>{ HeadLines.Facilities}</StyledH1Container>
        <StyledResultFilter>
          {hasActiveFilters && (
          <StyledResetFilter onClick={clearFilters}>
            reset filter
          </StyledResetFilter>
          )}
          <p>{t('XResults', { numberOfResults: numberOfObjects })}</p>
        </StyledResultFilter>
      </StyledHeadlineText>

      {loading ? (
        <StyledSpinnerContainer>
          <StyledSpinner icon={faSpinner} size="5x" />
        </StyledSpinnerContainer>
      ) : (
        <StyledList>
          {itemList.map(item => (
            <SearchResultListItem
              size={{ mobile: 6 / 12, tablet: 6 / 18, desktop: listItemDesktopSize / 12 }}
              key={`result-list-item-${item.id}`}
              result={item}
            />
          ))}
        </StyledList>
      )}
    </StyledWrapper>
  );
};

const mapStateToProps = (): {} => ({});

export default connect(
  mapStateToProps,
  { changeTitle }
)(SearchResultList);
